import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { ReportHtml } from 'tms-lib';
import {
  SalesInvoiceForCreditNote,
  SalesInvoiceHeaderDetails,
  SalesInvoiceHeader,
  SalesInvoicePaymentDueDate,
  SalesInvoicesCreated,
  SalesInvoiceCreditNote,
  PostingDocumentSummary,
  SagePosting,
  SalesCommissionInvoicesCreated,
  SalesInvoiceItemAddRemove,
  SalesInvoiceNumber,
  SalesInvoiceItems,
  SalesInvoiceEmailContacts,
  SalesInvoiceUnpost,
  SalesInvoiceUnpostResult,
} from '../../../models';
import { TmsWebBaseEditCreateService } from '../../base';

@Injectable({
  providedIn: 'root',
})
export class SalesInvoiceHeaderService extends TmsWebBaseEditCreateService<
  SalesInvoiceHeader,
  SalesInvoiceHeaderDetails
> {
  public refreshDetails = new Subject<void>();

  constructor() {
    super('SalesInvoiceHeader', $localize`Sales Invoice`);
  }

  createSalesInvoice(ids: string[]): Observable<string[]> {
    return this.httpService.post<string[]>(`${this.apiUrl}/CreateSalesInvoice`, { ids });
  }

  createCommissionSalesInvoice(ids: string[]): Observable<string[]> {
    return this.httpService.post<string[]>(`${this.apiUrl}/CreateCommissionSalesInvoice`, { ids });
  }

  getCreatedSalesInvoices(ids: string[]): Observable<SalesInvoicesCreated[]> {
    return this.httpService.post<SalesInvoicesCreated[]>(`${this.apiUrl}/CreatedSalesInvoiceHeaders`, { ids });
  }

  getCreatedSalesCommissionInvoices(ids: string[]): Observable<SalesCommissionInvoicesCreated[]> {
    return this.httpService.post<SalesCommissionInvoicesCreated[]>(
      `${this.apiUrl}/CreatedSalesCommissionInvoiceHeaders`,
      { ids },
    );
  }

  getSalesInvoiceNumber(id: string): Observable<SalesInvoiceNumber> {
    return this.httpService.get<SalesInvoiceNumber>(`${this.apiUrl}/GetSalesInvoiceNumber?id=${id}`);
  }

  getSalesInvoiceReportHtml(id: string, includeStyles: boolean): Observable<string> {
    return this.httpService
      .get<ReportHtml>(`${this.apiUrl}/SalesInvoiceReportHtml/${id}?includeStyles=${includeStyles}`)
      .pipe(map((model: ReportHtml) => model.html));
  }

  getSalesInvoiceItemsForInvoice(id: string): Observable<SalesInvoiceItems[]> {
    return this.httpService.get<SalesInvoiceItems[]>(`${this.apiUrl}/SalesInvoiceItemsForInvoice?id=${id}`);
  }

  getSalesInvoiceEmailContacts(id: string): Observable<SalesInvoiceEmailContacts> {
    return this.httpService.get<SalesInvoiceEmailContacts>(`${this.apiUrl}/SalesInvoiceEmailContacts?id=${id}`);
  }

  getPaymentDueDate(model: SalesInvoicePaymentDueDate): Observable<SalesInvoicePaymentDueDate> {
    return this.httpService.post<SalesInvoicePaymentDueDate>(`${this.apiUrl}/GetPaymentDueDate`, model);
  }

  getSalesInvoicesForCreditNote(search: string): Observable<SalesInvoiceForCreditNote[]> {
    search = search?.trim();

    return this.httpService.get<SalesInvoiceForCreditNote[]>(
      `${this.apiUrl}/SalesInvoicesForCreditNote?search=${search}`,
    );
  }

  /** The posting lines - the rows of the Sage file, several per invoice. */
  getSalesInvoicesForPosting(): Observable<SagePosting[]> {
    return this.httpService.get<SagePosting[]>(`${this.apiUrl}/SalesInvoicesForPosting`);
  }

  /** The Sales Invoice Posting screen's list - one row per invoice waiting to post. */
  getSalesInvoicesForPostingSummary(): Observable<PostingDocumentSummary[]> {
    return this.httpService.get<PostingDocumentSummary[]>(`${this.apiUrl}/SalesInvoicesForPostingSummary`);
  }

  postSalesInvoices(ids: string[]): Observable<SagePosting[]> {
    return this.httpService.post<SagePosting[]>(`${this.apiUrl}/PostSalesInvoices`, { ids });
  }

  // Sales -> Un-post Invoices: the invoices currently POSTED, and putting a selection back to
  // the status each held before posting (4Spaces additional points, Sep 2026, point 4).
  getPostedSalesInvoices(): Observable<SalesInvoiceUnpost[]> {
    return this.httpService.get<SalesInvoiceUnpost[]>(`${this.apiUrl}/PostedSalesInvoices`);
  }

  unpostSalesInvoices(ids: string[]): Observable<SalesInvoiceUnpostResult> {
    return this.httpService.post<SalesInvoiceUnpostResult>(`${this.apiUrl}/UnpostSalesInvoices`, { ids });
  }

  /** Removes the invoices from Xero (delete a draft, void an approved unpaid one), then un-posts them. */
  withdrawFromXero(ids: string[]): Observable<SalesInvoiceUnpostResult> {
    return this.httpService.post<SalesInvoiceUnpostResult>(`${this.apiUrl}/WithdrawFromXero`, { ids });
  }

  emailCSVFile(): Observable<any> {
    return this.httpService.get(`${this.apiUrl}/EmailCSVFile`);
  }

  emailSalesAnalysisCSVFile(fromDate: Date, toDate: Date): Observable<any> {
    return this.httpService.get(
      `${this.apiUrl}/EmailSalesAnalysisCSVFile?fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`,
    );
  }

  getSalesInvoiceCreditNotes(fromDate: Date, toDate: Date): Observable<SalesInvoiceCreditNote[]> {
    return this.httpService.get<SalesInvoiceCreditNote[]>(
      `${this.apiUrl}/SalesInvoiceCreditNotes?fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`,
    );
  }

  updateSalesInvoicePrice(id: string): Observable<string> {
    return this.httpService.post<string>(`${this.apiUrl}/UpdateSalesInvoicePrice`, { id });
  }

  // Closes an unposted invoice that has no goods left on it; the API refuses anything else.
  voidInvoice(id: string): Observable<void> {
    return this.httpService.post<void>(`${this.apiUrl}/Void/${id}`, {});
  }

  removeFromSalesInvoice(salesInvoiceItemAddRemove: SalesInvoiceItemAddRemove): Observable<string> {
    return this.httpService.post<string>(`${this.apiUrl}/RemoveFromSalesInvoice`, salesInvoiceItemAddRemove);
  }

  addToSalesInvoice(salesInvoiceItemAddRemove: SalesInvoiceItemAddRemove): Observable<string> {
    return this.httpService.post<string>(`${this.apiUrl}/AddToSalesInvoice`, salesInvoiceItemAddRemove);
  }
}
